import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { updateStore } from '@/services/storeService'
import * as commerce from '@/services/commerceService'
import { effectiveWalletCurrency } from '@/lib/storeWallet'
import { useCurrentStore } from '@/context/CurrentStoreContext'
import { fetchTransactions } from '@/services/transactionService'
import { formatMoneyFromUnknown, resolveCurrencyCode } from '@/lib/money'
import { Card, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { getUserFacingErrorMessage } from '@/utils/userFacingError'
import { FeatureToggle } from '@/components/ui/FeatureToggle'
import { SkeletonCommerceHub } from '@/components/ui/Skeleton'

type WalletActivityRow = {
  kind: string
  createdAt?: unknown
  currency?: string
  amount?: unknown
  orderNumber?: string | number
  reference?: string
  status?: string
  rawRef?: string
  channelLabel?: string
}

function formatActivityWhen(iso: unknown): string {
  if (iso == null) return '—'
  try {
    const d = iso instanceof Date ? iso : new Date(String(iso))
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return '—'
  }
}

function walletActivityDescription(row: WalletActivityRow): string {
  if (row.kind === 'ledger') {
    const e = String(row.status || '')
    if (e === 'credit_payment') return 'Online order — wallet credit'
    if (e === 'debit_withdrawal') return 'Withdrawal payout (wallet debit)'
    return e ? `Ledger (${e})` : 'Ledger entry'
  }
  if (row.kind === 'withdrawal') return `Withdrawal request (${String(row.status || '—')})`
  if (row.kind === 'pos_sale') return 'In-store (POS) sale'
  return row.kind
}

export default function CommerceHubPage() {
  const { currentStoreId: storeId, storeRecord, loading: storeContextLoading, refreshStores } = useCurrentStore()
  const [wallet, setWallet] = useState<commerce.WalletSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [onlinePaymentsEnabled, setOnlinePaymentsEnabled] = useState(true)
  const [payOnDeliveryEnabled, setPayOnDeliveryEnabled] = useState(false)
  const [fulfillmentDeliveryEnabled, setFulfillmentDeliveryEnabled] = useState(true)
  const [fulfillmentPickupEnabled, setFulfillmentPickupEnabled] = useState(false)
  const [savingFulfillment, setSavingFulfillment] = useState(false)
  const [fulfillmentToast, setFulfillmentToast] = useState<string | null>(null)
  const [walletActivity, setWalletActivity] = useState<WalletActivityRow[]>([])

  const storeCurrency = effectiveWalletCurrency(storeRecord)

  const refresh = useCallback(async () => {
    if (!storeId) return
    setLoading(true)
    setError(null)
    try {
      const walletCur = effectiveWalletCurrency(storeRecord)
      const [w, tx] = await Promise.all([
        commerce.fetchStoreWallet(storeId, walletCur),
        fetchTransactions({ storeId, page: 1, limit: 60, channel: 'all' }),
      ])
      setWallet(w)
      const cur = String(w?.currency || '').toUpperCase()
      setWalletActivity(
        (tx.items as WalletActivityRow[]).filter(
          (row) => String(row.currency || '').toUpperCase() === cur,
        ),
      )
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load commerce data'))
    } finally {
      setLoading(false)
    }
  }, [storeId, storeRecord])

  useEffect(() => {
    if (storeId) void refresh()
  }, [storeId, refresh])

  useEffect(() => {
    if (storeRecord) {
      setOnlinePaymentsEnabled(storeRecord.onlinePaymentsEnabled !== false)
      setPayOnDeliveryEnabled(Boolean(storeRecord.payOnDeliveryEnabled))
      setFulfillmentDeliveryEnabled(storeRecord.fulfillmentDeliveryEnabled !== false)
      setFulfillmentPickupEnabled(Boolean(storeRecord.fulfillmentPickupEnabled))
    }
  }, [storeRecord])

  async function handleSaveFulfillment() {
    if (!storeId) return
    setSavingFulfillment(true)
    setError(null)
    setFulfillmentToast(null)
    try {
      await updateStore(storeId, {
        onlinePaymentsEnabled,
        payOnDeliveryEnabled,
        fulfillmentDeliveryEnabled,
        fulfillmentPickupEnabled,
      })
      setFulfillmentToast('Checkout & fulfillment options saved.')
      await refreshStores()
      await refresh()
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to save fulfillment options'))
    } finally {
      setSavingFulfillment(false)
    }
  }

  const showInitialSkeleton = storeContextLoading || (loading && wallet === null)

  if (showInitialSkeleton) {
    return <SkeletonCommerceHub />
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Payments & wallet"
          description="Balances use this store’s trading currency (when set); credits from online orders post to the wallet row for that same currency."
        />
        {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
        {fulfillmentToast ? (
          <p className="mb-4 text-sm text-emerald-700 dark:text-emerald-400">{fulfillmentToast}</p>
        ) : null}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Button type="button" variant="secondary" onClick={() => refresh()} disabled={loading || !storeId}>
            Refresh
          </Button>
        </div>

        {wallet ? (
          <div className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Available</p>
              <p className="text-lg font-semibold tabular-nums">
                {wallet.currency} {wallet.availableBalance}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">Ready to withdraw</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">On the way</p>
              <p className="text-lg font-semibold tabular-nums">
                {wallet.currency} {wallet.pendingSettlementBalance ?? '0'}
              </p>
              {wallet.nextSettlementAt ? (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Expected in your account by{' '}
                  {new Date(wallet.nextSettlementAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </p>
              ) : (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Will drop into Available soon
                </p>
              )}
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Total credited</p>
              <p className="text-lg font-semibold tabular-nums">
                {wallet.currency} {wallet.totalCredited}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Withdrawn</p>
              <p className="text-lg font-semibold tabular-nums">
                {wallet.currency} {wallet.totalWithdrawn}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Pending withdrawals</p>
              <p className="text-lg font-semibold tabular-nums">
                {wallet.currency} {wallet.pendingWithdrawalAmount}
              </p>
              <Link to="/withdrawals" className="mt-2 inline-block text-xs text-primary hover:underline">
                Manage withdrawals →
              </Link>
            </div>
          </div>
        ) : null}

        {wallet ? (
          <div className="mb-8">
            <h3 className="mb-1 text-sm font-semibold text-foreground">
              What makes up this balance ({wallet.currency})
            </h3>
            <p className="mb-3 text-xs text-muted-foreground">
              Recent ledger movements for this store in this currency: online payments credited to the wallet,
              in-store sales, withdrawal requests, and completed payouts. Amounts match the transactions list.
            </p>
            {walletActivity.length === 0 ? (
              <p className="text-sm text-muted-foreground">No movements in this currency yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="bg-muted/30 text-left text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-medium">S/N</th>
                      <th className="px-4 py-3 font-medium">When</th>
                      <th className="px-4 py-3 font-medium">Description</th>
                      <th className="px-4 py-3 font-medium">Order / reference</th>
                      <th className="px-4 py-3 font-medium">Amount</th>
                      <th className="px-4 py-3 font-medium">Detail</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-card">
                    {walletActivity.map((row, i) => (
                      <tr key={row.rawRef ?? `${row.kind}-${row.reference}-${i}`}>
                        <td className="px-4 py-3 tabular-nums text-muted-foreground">{i + 1}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs">{formatActivityWhen(row.createdAt)}</td>
                        <td className="max-w-[220px] px-4 py-3 text-xs">{walletActivityDescription(row)}</td>
                        <td className="max-w-[180px] truncate px-4 py-3 font-mono text-xs" title={String(row.orderNumber ?? row.reference ?? '')}>
                          {row.kind === 'ledger' && row.orderNumber && row.orderNumber !== '-'
                            ? String(row.orderNumber)
                            : String(row.reference ?? '—')}
                        </td>
                        <td className="px-4 py-3 tabular-nums">
                          {row.amount != null && row.amount !== ''
                            ? formatMoneyFromUnknown(row.amount, resolveCurrencyCode(row.currency))
                            : '—'}
                        </td>
                        <td className="px-4 py-3">
                          {row.rawRef ? (
                            <Link
                              to={`/transactions/${encodeURIComponent(row.rawRef)}`}
                              className="text-primary text-xs hover:underline"
                            >
                              View
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              Showing the latest 60 movements for this store (same currency as the summary). For filters and the
              full list, open{' '}
              <Link to="/transactions" className="text-primary underline-offset-4 hover:underline">
                Transactions
              </Link>
              .
            </p>
          </div>
        ) : null}

        <div className="mb-8 rounded-xl border border-border bg-muted/15 p-4 space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Checkout & fulfillment</h3>
          <p className="text-xs text-muted-foreground">
            Control how customers receive orders and whether they can pay on delivery. Add pickup points
            under{' '}
            <Link to="/pickup-locations" className="font-medium text-primary underline-offset-4 hover:underline">
              Pickup locations
            </Link>{' '}
            before enabling pickup below.
          </p>
          <FeatureToggle
            label="Accept online payments"
            description={
              storeRecord?.onlinePaymentsAvailable === false
                ? `Kartly has no active payment providers for ${storeCurrency} yet. Contact support if you expect card payments.`
                : `Customers can pay with Kartly-supported methods for ${storeCurrency}.`
            }
            checked={onlinePaymentsEnabled}
            onChange={setOnlinePaymentsEnabled}
          />
          <FeatureToggle
            label="Home delivery"
            description="Customers choose a saved address at checkout."
            checked={fulfillmentDeliveryEnabled}
            onChange={setFulfillmentDeliveryEnabled}
          />
          <FeatureToggle
            label="Pickup"
            description="Customers choose a pickup point from your pickup locations list."
            checked={fulfillmentPickupEnabled}
            onChange={setFulfillmentPickupEnabled}
          />
          <FeatureToggle
            label="Pay on delivery or at pickup"
            description="Customers pay when they receive or collect the order (shown as “Pay at pickup” for pickup orders). No platform wallet credit for these orders."
            checked={payOnDeliveryEnabled}
            onChange={setPayOnDeliveryEnabled}
          />
          <Button
            type="button"
            onClick={() => void handleSaveFulfillment()}
            disabled={savingFulfillment || !storeId}
          >
            {savingFulfillment ? 'Saving…' : 'Save checkout options'}
          </Button>
        </div>
      </Card>
    </div>
  )
}
